import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Anchor, Ship, Minus, Plus, Calendar as CalendarIcon, Clock, MapPin } from 'lucide-react';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Textarea } from './ui/textarea';
import { format, addHours, isBefore, getMonth } from 'date-fns';
import { de } from 'date-fns/locale';
import { 
  BOAT_TYPES, 
  ADDONS, 
  TIME_SLOTS,
  LOCATIONS,
  WIZARD_STEPS,
  TEXTS,
  SEASON_START_MONTH,
  SEASON_END_MONTH,
  MIN_LEAD_TIME_HOURS,
  TOUR_DURATION_HOURS
} from '../constants';
import { RIBBoat, ExclusiveBoat } from './svg/BoatSVG';

// Realistic Cyprus Map Component
const CyprusMap = ({ selectedLocation, onSelect }) => {
  return (
    <div className="relative w-full max-w-md mx-auto">
      <svg viewBox="0 0 500 250" className="w-full">
        {/* Realistic Cyprus island shape */}
        <path
          d="M30 140 
             C40 110, 70 85, 120 75
             C160 67, 200 60, 240 58
             C280 56, 320 58, 360 65
             C400 72, 430 85, 455 100
             L480 95
             C485 100, 488 108, 485 115
             L470 125
             C450 135, 420 145, 380 155
             C340 165, 290 172, 240 175
             C190 178, 140 175, 100 168
             C60 161, 35 150, 30 140
             Z"
          fill="#E0F2FE"
          stroke="#0EA5E9"
          strokeWidth="2"
        />
        
        {/* Karpass Peninsula (finger extending east) */}
        <path
          d="M455 100 
             L480 95
             C485 100, 488 108, 485 115
             L470 125
             C465 118, 460 110, 455 105
             Z"
          fill="#E0F2FE"
          stroke="#0EA5E9"
          strokeWidth="2"
        />
        
        {/* Mountains/terrain indication */}
        <path
          d="M150 100 Q200 80, 250 95 Q300 85, 350 100"
          stroke="#BAE6FD"
          strokeWidth="3"
          fill="none"
          opacity="0.5"
        />
        
        {/* Polis marker (northwest) */}
        <g 
          className="cursor-pointer"
          onClick={() => onSelect('polis')}
        >
          <motion.circle
            cx="95"
            cy="105"
            r={selectedLocation === 'polis' ? 20 : 16}
            fill={selectedLocation === 'polis' ? '#FACC15' : '#0EA5E9'}
            stroke="white"
            strokeWidth="3"
            animate={{ scale: selectedLocation === 'polis' ? [1, 1.15, 1] : 1 }}
            transition={{ duration: 1.2, repeat: Infinity }}
          />
          <text x="95" y="110" textAnchor="middle" fontSize="12" fill="white" fontWeight="bold">P</text>
        </g>
        <text x="95" y="145" textAnchor="middle" fontSize="12" fill="#0F172A" fontWeight="600">Polis</text>
        
        {/* Ayia Napa marker (southeast) */}
        <g 
          className="cursor-pointer"
          onClick={() => onSelect('ayia-napa')}
        >
          <motion.circle
            cx="420"
            cy="130"
            r={selectedLocation === 'ayia-napa' ? 20 : 16}
            fill={selectedLocation === 'ayia-napa' ? '#FACC15' : '#0EA5E9'}
            stroke="white"
            strokeWidth="3"
            animate={{ scale: selectedLocation === 'ayia-napa' ? [1, 1.15, 1] : 1 }}
            transition={{ duration: 1.2, repeat: Infinity }}
          />
          <text x="420" y="135" textAnchor="middle" fontSize="11" fill="white" fontWeight="bold">AN</text>
        </g>
        <text x="420" y="165" textAnchor="middle" fontSize="12" fill="#0F172A" fontWeight="600">Ayia Napa</text>
        
        {/* Cyprus label */}
        <text x="250" y="120" textAnchor="middle" fontSize="16" fill="#64748B" fontStyle="italic">Zypern</text>
        
        {/* Compass */}
        <g transform="translate(50, 200)">
          <circle cx="0" cy="0" r="12" fill="white" stroke="#94A3B8" strokeWidth="1" />
          <text x="0" y="4" textAnchor="middle" fontSize="10" fill="#64748B" fontWeight="bold">N</text>
        </g>
      </svg>
    </div>
  );
};

const WizardSteps = ({ selections, setSelections, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  
  const steps = WIZARD_STEPS;
  const totalSteps = steps.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  // Min date: 48 hours from now
  const minDate = addHours(new Date(), MIN_LEAD_TIME_HOURS);
  
  // Check if date is in season
  const isInSeason = (date) => {
    const month = getMonth(date);
    return month >= SEASON_START_MONTH && month <= SEASON_END_MONTH;
  };

  // Date validation
  const isDateDisabled = (date) => {
    return isBefore(date, minDate) || !isInSeason(date);
  };

  // Get max people based on boat type
  const maxPeople = selections.boatType ? BOAT_TYPES[selections.boatType].maxPeople : 12;

  // Handle next/back
  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      onComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  // Check if can proceed
  const canProceed = () => {
    switch (steps[currentStep].id) {
      case 'boat': return !!selections.boatType;
      case 'location': return !!selections.location;
      case 'date': return !!selections.date;
      case 'time': return !!selections.time;
      case 'people': return selections.people >= 1;
      case 'addon': return !!selections.addon;
      case 'wish': return true; // Optional
      default: return true;
    }
  };

  // Adjust people count if boat type changes
  useEffect(() => {
    if (selections.people > maxPeople) {
      setSelections(prev => ({ ...prev, people: maxPeople }));
    }
  }, [selections.boatType, maxPeople, selections.people, setSelections]);

  const renderStep = () => {
    const step = steps[currentStep];
    
    switch (step.id) {
      case 'boat':
        return (
          <div className="space-y-6" data-testid="boat-step">
            <div className="text-center mb-4">
              <h2 className="text-2xl font-black text-slate-900 mb-2" style={{ fontFamily: 'Nunito' }}>
                {TEXTS.boatStepTitle}
              </h2>
              <p className="text-slate-600">{TEXTS.boatStepSubtitle}</p>
            </div>
            
            {/* Boat cards - with significant vertical spacing */}
            <div className="flex flex-col gap-8">
              {Object.values(BOAT_TYPES).map((boat, index) => (
                <motion.button
                  key={boat.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelections(prev => ({ ...prev, boatType: boat.id }))}
                  className={`card-select w-full text-left ${
                    selections.boatType === boat.id ? 'selected' : ''
                  }`}
                  data-testid={`boat-option-${boat.id}`}
                >
                  <div className="flex flex-col gap-4">
                    {/* Boat illustration */}
                    <div className="flex justify-center py-3">
                      {boat.id === 'rib' ? <RIBBoat size="md" /> : <ExclusiveBoat size="md" />}
                    </div>
                    
                    {/* Boat info */}
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-2 mb-1">
                        <span className="text-xl font-bold text-slate-900">{boat.name}</span>
                        {selections.boatType === boat.id && (
                          <motion.span
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="text-sky-500 text-xl"
                          >
                            ✓
                          </motion.span>
                        )}
                      </div>
                      <p className="text-sm font-semibold text-sky-600 mb-2">{boat.tagline}</p>
                      <p className="text-sm text-slate-600 mb-3">{boat.description}</p>
                      <div className="flex flex-wrap justify-center gap-2">
                        {boat.benefits.map((benefit, idx) => (
                          <span 
                            key={idx}
                            className="text-xs bg-sky-100 text-sky-700 px-2 py-1 rounded-full"
                          >
                            {benefit}
                          </span>
                        ))}
                      </div>
                      <p className="text-xs text-slate-500 mt-3">Max. {boat.maxPeople} Personen</p>
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>
          </div>
        );

      case 'location':
        return (
          <div className="space-y-6" data-testid="location-step">
            <div className="text-center mb-4">
              <h2 className="text-2xl font-black text-slate-900 mb-2" style={{ fontFamily: 'Nunito' }}>
                {TEXTS.locationStepTitle}
              </h2>
              <p className="text-slate-600">{TEXTS.locationStepSubtitle}</p>
            </div>
            
            {/* Cyprus Map */}
            <CyprusMap 
              selectedLocation={selections.location}
              onSelect={(loc) => setSelections(prev => ({ ...prev, location: loc }))}
            />
            
            {/* Location cards - with significant vertical spacing */}
            <div className="flex flex-col gap-6 mt-6">
              {LOCATIONS.map((loc, index) => (
                <motion.button
                  key={loc.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelections(prev => ({ ...prev, location: loc.id }))}
                  className={`w-full glass rounded-2xl p-5 flex items-center gap-4 ${
                    selections.location === loc.id ? 'border-2 border-sky-400 bg-sky-50/50' : ''
                  }`}
                  data-testid={`location-option-${loc.id}`}
                >
                  <span className="text-4xl">{loc.emoji}</span>
                  <div className="text-left flex-1">
                    <span className="text-xl font-bold text-slate-900 block">{loc.name}</span>
                    <span className="text-sm text-slate-600">{loc.description}</span>
                  </div>
                  {selections.location === loc.id && (
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="text-sky-500 text-xl"
                    >
                      ✓
                    </motion.span>
                  )}
                </motion.button>
              ))}
            </div>
          </div>
        );
        
      case 'date':
        return (
          <div className="space-y-4" data-testid="date-step">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-black text-slate-900 mb-2" style={{ fontFamily: 'Nunito' }}>
                {TEXTS.dateStepTitle}
              </h2>
              <p className="text-slate-600">{TEXTS.dateStepSubtitle}</p>
            </div>
            
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <button
                  className={`w-full glass rounded-2xl p-4 flex items-center gap-3 text-left ${
                    selections.date ? 'border-2 border-sky-400' : ''
                  }`}
                  data-testid="date-picker-trigger"
                >
                  <CalendarIcon className="w-6 h-6 text-sky-500" />
                  <div>
                    {selections.date ? (
                      <span className="text-lg font-bold text-slate-900">
                        {format(new Date(selections.date), 'PPP', { locale: de })}
                      </span>
                    ) : (
                      <span className="text-slate-500">Datum auswählen...</span>
                    )}
                  </div>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="center">
                <Calendar
                  mode="single"
                  selected={selections.date ? new Date(selections.date) : undefined}
                  onSelect={(date) => {
                    if (date) {
                      setSelections(prev => ({ ...prev, date: format(date, 'yyyy-MM-dd') }));
                      setDatePickerOpen(false);
                    }
                  }}
                  disabled={isDateDisabled}
                  initialFocus
                  locale={de}
                />
              </PopoverContent>
            </Popover>
            
            <p className="text-xs text-center text-slate-500">
              Saison: Ganzjährig (Demo-Modus)
            </p>
          </div>
        );
        
      case 'time':
        return (
          <div className="space-y-4" data-testid="time-step">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-black text-slate-900 mb-2" style={{ fontFamily: 'Nunito' }}>
                {TEXTS.timeStepTitle}
              </h2>
              <p className="text-slate-600">{TEXTS.timeStepSubtitle}</p>
            </div>
            
            <div className="space-y-6">
              {TIME_SLOTS.map((slot) => (
                <motion.button
                  key={slot.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelections(prev => ({ ...prev, time: slot.value }))}
                  className={`w-full glass rounded-2xl p-4 flex items-center gap-3 ${
                    selections.time === slot.value ? 'border-2 border-sky-400 bg-sky-50/50' : ''
                  }`}
                  data-testid={`time-option-${slot.id}`}
                >
                  <Clock className="w-6 h-6 text-sky-500" />
                  <span className="text-lg font-semibold text-slate-900">{slot.label}</span>
                  {selections.time === slot.value && (
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="ml-auto text-sky-500"
                    >
                      ✓
                    </motion.span>
                  )}
                </motion.button>
              ))}
            </div>
            
            <p className="text-xs text-center text-slate-500">
              Dauer: {TOUR_DURATION_HOURS} Stunden
            </p>
          </div>
        );
        
      case 'people':
        return (
          <div className="space-y-4" data-testid="people-step">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-black text-slate-900 mb-2" style={{ fontFamily: 'Nunito' }}>
                {TEXTS.peopleStepTitle}
              </h2>
              <p className="text-slate-600">{TEXTS.peopleStepSubtitle}</p>
            </div>
            
            <div className="glass rounded-3xl p-8 flex flex-col items-center">
              <div className="flex items-center gap-6">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setSelections(prev => ({ 
                    ...prev, 
                    people: Math.max(1, prev.people - 1) 
                  }))}
                  disabled={selections.people <= 1}
                  className="stepper-btn"
                  data-testid="people-minus"
                >
                  <Minus className="w-6 h-6" />
                </motion.button>
                
                <motion.div
                  key={selections.people}
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  className="w-24 text-center"
                >
                  <span className="text-6xl font-black text-slate-900" data-testid="people-count">
                    {selections.people}
                  </span>
                </motion.div>
                
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setSelections(prev => ({ 
                    ...prev, 
                    people: Math.min(maxPeople, prev.people + 1) 
                  }))}
                  disabled={selections.people >= maxPeople}
                  className="stepper-btn"
                  data-testid="people-plus"
                >
                  <Plus className="w-6 h-6" />
                </motion.button>
              </div>
              
              <p className="text-sm text-slate-500 mt-4">
                Max. {maxPeople} Personen für {BOAT_TYPES[selections.boatType]?.name || 'dieses Boot'}
              </p>
            </div>
          </div>
        );
        
      case 'addon':
        return (
          <div className="space-y-4" data-testid="addon-step">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-black text-slate-900 mb-2" style={{ fontFamily: 'Nunito' }}>
                {TEXTS.addonStepTitle}
              </h2>
              <p className="text-slate-600">{TEXTS.addonStepSubtitle}</p>
            </div>
            
            <div className="grid gap-3">
              {ADDONS.map((addon) => (
                <motion.button
                  key={addon.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelections(prev => ({ ...prev, addon: addon.id }))}
                  className={`glass rounded-2xl p-4 flex items-center gap-4 ${
                    selections.addon === addon.id ? 'border-2 border-sky-400 bg-sky-50/50' : ''
                  }`}
                  data-testid={`addon-option-${addon.id}`}
                >
                  <span className="text-3xl">{addon.emoji}</span>
                  <span className="text-lg font-semibold text-slate-900">{addon.name}</span>
                  {selections.addon === addon.id && (
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="ml-auto text-sky-500"
                    >
                      ✓
                    </motion.span>
                  )}
                </motion.button>
              ))}
            </div>
          </div>
        );
        
      case 'wish':
        return (
          <div className="space-y-4" data-testid="wish-step">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-black text-slate-900 mb-2" style={{ fontFamily: 'Nunito' }}>
                {TEXTS.wishStepTitle}
              </h2>
              <p className="text-slate-600">{TEXTS.wishStepSubtitle}</p>
            </div>
            
            <Textarea
              value={selections.customWish || ''}
              onChange={(e) => setSelections(prev => ({ ...prev, customWish: e.target.value }))}
              placeholder={TEXTS.wishPlaceholder}
              className="glass rounded-2xl p-4 min-h-[120px] text-lg resize-none border-2 border-transparent focus:border-sky-400"
              data-testid="wish-input"
            />
            
            <p className="text-xs text-center text-slate-500">
              Optional – kannsch auch leer lasse
            </p>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="wizard-container">
      {/* Progress bar */}
      <div className="sticky top-0 z-20 p-4 glass">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-600">
            Schritt {currentStep + 1} von {totalSteps}
          </span>
          <span className="text-sm text-slate-500">
            {steps[currentStep].emoji} {steps[currentStep].label}
          </span>
        </div>
        <div className="progress-bar">
          <motion.div 
            className="progress-fill"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
      </div>

      {/* Step content */}
      <div className="flex-1 p-6 pb-32 overflow-y-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.25 }}
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation */}
      <div className="nav-footer">
        <div className="flex gap-3">
          {currentStep > 0 && (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleBack}
              className="flex-1 py-4 px-6 rounded-full border-2 border-slate-200 text-slate-700 font-semibold flex items-center justify-center gap-2"
              data-testid="back-button"
            >
              <ChevronLeft className="w-5 h-5" />
              {TEXTS.backButton}
            </motion.button>
          )}
          
          <motion.button
            whileHover={{ scale: canProceed() ? 1.02 : 1 }}
            whileTap={{ scale: canProceed() ? 0.98 : 1 }}
            onClick={handleNext}
            disabled={!canProceed()}
            className={`flex-1 btn-sun flex items-center justify-center gap-2 ${
              !canProceed() ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            data-testid="next-button"
          >
            {currentStep === totalSteps - 1 ? 'Fertig!' : TEXTS.nextButton}
            <ChevronRight className="w-5 h-5" />
          </motion.button>
        </div>
      </div>
    </div>
  );
};

export default WizardSteps;
